import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import UserRoleBadge from "../../Components/UserRoleBadge";
import {
  AccountDTO,
  AccountPunishment,
  AccountSessionDTO,
  CharacterDTO,
  UserBadge,
} from "../../Networking/Interface/Account/AccountInterface";
import { InitialsAPI } from "../../Networking/InitialsAPI";
import { SunsetAPIAccountContext } from "../../Networking/Interface/Account/AccountAPIProvider";
import Banned from "../../ui/Banned";
import GetSessionButton from "../../Launcher/GetSessionButton";
import BanUser from "../../Staff/Actions/Ban";
import UnbanUser from "../../Staff/Actions/Unban";
import ResetSpawnButton from "../../Staff/Actions/ResetSpawn";
import ForceUser from "../../Staff/Actions/ForceUser";
import SetAdminLevelButton from "../../Staff/Actions/SetAdmin";
import SetAccountNameButton from "../../Staff/Actions/AccountNameChange";
import AwardBadge from "../../Staff/Actions/AwardBadge";
import { UserContext } from "../../User/UserContext";
import { DisconnectReason, UserRole } from "../../Utils/enums";
import { formatDateForDisplay } from "../../Utils/DateTimeUtils";
import { CharacterView } from "./CharacterView";
import { AccountActivityView } from "./AccountActivityView";
import PunishmentsView from "../../Staff/Actions/PunishmentView";
import NotesLookup from "../../Staff/Actions/NotesLookup";
import { MdAccessTime, MdCalendarToday, MdCircle, MdEdit } from "react-icons/md";
import { PfpModal } from "../../Components/PfpModal";
import { OnlineTooltip } from "../../Components/OnlineTooltip";
import styles from "./AccountViewNew.module.css";

interface AccountViewNewProps {
  accountDTO: AccountDTO;
}

type MainTab = "characters" | "activity" | "staff" | "admin";

// =============================================================================
// Component
// =============================================================================

export const AccountViewNew: React.FC<AccountViewNewProps> = ({
  accountDTO,
}) => {
  const api = useContext(SunsetAPIAccountContext);
  const { user } = useContext(UserContext);

  // ---- state ---------------------------------------------------------------
  const [avatarUrl, setAvatarUrl] = useState<string>();
  const [lastSession, setLastSession] = useState<AccountSessionDTO | null>(null);
  const [isBanned, setIsBanned] = useState<boolean | AccountPunishment>(false);
  const [badges, setBadges] = useState<UserBadge[]>([]);
  const [characters, setCharacters] = useState<CharacterDTO[]>([]);
  const [activeCharId, setActiveCharId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<MainTab>("characters");
  const [modalKey, setModalKey] = useState(0);
  const [badgeRefreshKey, setBadgeRefreshKey] = useState(0);
  const [pfpModalOpen, setPfpModalOpen] = useState(false);

  // ---- update pfp handler ----
  const handlePfpUpdate = async (url: string) => {
    if (!api) throw new Error("API unavailable");
    const res = await api.updatePfp(url);
    if (res.success) {
      setAvatarUrl(url);
      await fetchAccountData();
    } else {
      throw new Error((res.data && res.data.message) || "Failed to update profile picture.");
    }
  };

  // ---- permissions ---------------------------------------------------------
  const hasRole = useCallback(
    (min: number) => !!user && user.role >= min,
    [user]
  );
  const isStaffOrSelf = useMemo(
    () =>
      (!!user && user.role >= UserRole.Mod) ||
      (!!user && accountDTO.username === user.username),
    [user, accountDTO.username]
  );

  // ---- data fetch ----------------------------------------------------------
  useEffect(() => {
    if (accountDTO.pfp) {
      setAvatarUrl(accountDTO.pfp);
    } else if (characters.length > 0) {
      let char: CharacterDTO | undefined;
      if (lastSession && lastSession.characterName) {
        char = characters.find((c) => c.characterName === lastSession.characterName);
      }
      if (!char) char = characters[0];
      const skin = char?.lastSkin;
      if (skin !== undefined && skin !== null) {
        const idSkin = skin > 300 ? 26 : skin;
        setAvatarUrl(`${window.location.origin}/samp/headshots/${idSkin}.png`);
      } else {
        setAvatarUrl(
          new InitialsAPI().getAvatarURL({
            name: accountDTO.username.replace("_", "+"),
            size: 200,
          })
        );
      }
    } else {
      setAvatarUrl(
        new InitialsAPI().getAvatarURL({
          name: accountDTO.username.replace("_", "+"),
          size: 200,
        })
      );
    }
  }, [accountDTO, characters, lastSession]);

  const fetchAccountData = useCallback(async () => {
    try {
      const [sessionRes, banRes] = await Promise.all([
        api?.getLastAccountSession(accountDTO.id, accountDTO.username),
        api?.IsAccountBanned(accountDTO.id),
      ]);
      if (sessionRes?.success) setLastSession(sessionRes.data || null);
      if (banRes?.success) {
        setIsBanned(
          Array.isArray(banRes.data)
            ? banRes.data.length > 0
            : banRes.data || false
        );
      }
    } catch (err) {
      console.error("Error fetching account data:", err);
    }
  }, [api, accountDTO]);

  useEffect(() => {
    fetchAccountData();
  }, [fetchAccountData]);

  // badges
  useEffect(() => {
    const fetch = async () => {
      if (!api) return;
      const res = await api.getUserBadges(accountDTO.id);
      if (res.success && res.data) setBadges(res.data);
    };
    fetch();
  }, [api, accountDTO.id, badgeRefreshKey]);

  // characters
  useEffect(() => {
    const fetch = async () => {
      const res = await api?.getCharactersByAccountIDAndNAme(
        accountDTO.id,
        accountDTO.username
      );
      if (res?.success && res.data) {
        setCharacters(res.data);
        setActiveCharId(res.data[0]?.id ?? null);
      }
    };
    fetch();
  }, [api, accountDTO]);

  // ---- callbacks -----------------------------------------------------------
  const handleSuccess = useCallback(async () => {
    await fetchAccountData();
  }, [fetchAccountData]);

  const handleBadgeAwardSuccess = useCallback(() => {
    setBadgeRefreshKey((k) => k + 1);
  }, []);

  const toggleModalKey = () => setModalKey((k) => k + 1);

  // ---- derived -------------------------------------------------------------
  const activeChar = characters.find((c) => c.id === activeCharId) ?? null;

  // ---- render ---------------------------------------------------------------
  return (
    <div className={styles.wrapper}>
      {/* banned overlay */}
      {isBanned && <Banned account={accountDTO.username} />}

      {/* ================================================================== */}
      {/* 1. Hero Section with blurred PFP background                        */}
      {/* ================================================================== */}
      <div className={styles.hero}>
        {accountDTO.pfp ? (
          <div
            className={styles.heroBg}
            style={{ backgroundImage: `url(${accountDTO.pfp})` }}
          />
        ) : (
          <div className={styles.heroBgFallback} />
        )}

        <div className={styles.heroContent}>
          <div className={styles.heroAvatarWrap}>
            <img
              className={styles.heroAvatar}
              src={avatarUrl}
              alt={accountDTO.username}
            />
            {isStaffOrSelf && accountDTO.donatorLevel > 0 && (
              <button
                className={styles.editPfpBtn}
                title="Change profile picture"
                onClick={() => setPfpModalOpen(true)}
              >
                <MdEdit size={18} color="#111" />
              </button>
            )}
            <OnlineDotWithTooltip lastSession={lastSession} />
          </div>
          <PfpModal
            isOpen={pfpModalOpen}
            onClose={() => setPfpModalOpen(false)}
            onSubmit={handlePfpUpdate}
            donatorLevel={accountDTO.donatorLevel}
          />

          <div className={styles.heroInfo}>
            <h1 className={styles.heroUsername}>{accountDTO.username}</h1>
            <div className={styles.heroBadges}>
              <UserRoleBadge role={accountDTO.adminLevel} />
              {accountDTO.donatorLevel > 0 && (
                <span
                  className={styles.donatorBadge}
                  style={{
                    background:
                      accountDTO.donatorLevel === 1
                        ? "#cd7f32"
                        : accountDTO.donatorLevel === 2
                        ? "#c0c0c0"
                        : accountDTO.donatorLevel === 3
                        ? "#ffd700"
                        : "#e5e4e2",
                    color:
                      accountDTO.donatorLevel === 3 || accountDTO.donatorLevel === 4
                        ? "#111"
                        : "#fff",
                  }}
                >
                  {accountDTO.donatorLevel === 1
                    ? "Bronze"
                    : accountDTO.donatorLevel === 2
                    ? "Silver"
                    : accountDTO.donatorLevel === 3
                    ? "Gold"
                    : "Platinum"}
                </span>
              )}
            </div>
            <div className={styles.heroMeta}>
              <span className={styles.heroMetaItem}>
                <MdAccessTime size={14} /> {(accountDTO.totalPlaytime / 60).toFixed(1)}h played
              </span>
              <span className={styles.heroMetaItem}>
                <MdCalendarToday size={14} /> Joined{" "}
                {new Date(accountDTO.registrationDate).toLocaleDateString()}
              </span>
              {lastSession?.isOnline && (
                <span className={styles.heroMetaItem}>
                  <MdCircle size={10} color="#4caf50" /> Playing as {lastSession.characterName}
                </span>
              )}
              {isBanned && <span className={styles.bannedBadge}>Banned</span>}
            </div>
          </div>
        </div>
      </div>

      {/* ================================================================== */}
      {/* 2. Quick Stats Bar                                                 */}
      {/* ================================================================== */}
      <div className={styles.statsBar}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {(accountDTO.totalPlaytime / 60).toFixed(1)}h
          </div>
          <div className={styles.statLabel}>Total Playtime</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {new Date(accountDTO.registrationDate).toLocaleDateString()}
          </div>
          <div className={styles.statLabel}>Member Since</div>
        </div>
      </div>

      {/* ================================================================== */}
      {/* Badges showcase                                                    */}
      {/* ================================================================== */}
      {badges.length > 0 && (
        <div className={styles.badgesRow}>
          {badges.map((b) => (
            <span key={b.id} className={styles.badgePill}>
              <span className={styles.badgeIcon}>★</span>
              {b.name}
              <span className={styles.badgeTooltip}>{b.description}</span>
            </span>
          ))}
        </div>
      )}

      {/* ================================================================== */}
      {/* Section tabs                                                       */}
      {/* ================================================================== */}
      <div className={styles.tabs}>
        {(
          [
            ["characters", "Characters"],
            ["activity", "Activity"],
            ...(isStaffOrSelf ? [["staff", "Staff"]] : []),
            ...(hasRole(3) ? [["admin", "Admin Actions"]] : []),
          ] as [MainTab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            className={`${styles.tab} ${activeTab === key ? styles.tabActive : ""}`}
            onClick={() => setActiveTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ================================================================== */}
      {/* Tab: Characters                                                    */}
      {/* ================================================================== */}
      {activeTab === "characters" && (
        <div className={styles.tabContent}>
          {characters.length === 0 ? (
            <p className={styles.emptyState}>No characters found.</p>
          ) : (
            <>
              <div className={styles.charStrip}>
                {characters.map((c) => (
                  <div
                    key={c.id}
                    className={`${styles.charCard} ${activeCharId === c.id ? styles.charCardActive : ""}`}
                    onClick={() => setActiveCharId(c.id)}
                  >
                    <img
                      className={styles.charHeadshot}
                      src={`${process.env.PUBLIC_URL}/samp/headshots/${c.lastSkin > 300 ? 26 : c.lastSkin}.png`}
                      alt={c.characterName}
                    />
                    <span className={styles.charName}>{c.characterName}</span>
                    <span className={styles.charMeta}>
                      {(c.totalPlaytime / 60).toFixed(1)}h
                    </span>
                  </div>
                ))}
              </div>
              <div className={styles.charLayout}>
                {activeChar && <CharacterView character={activeChar} />}
              </div>
            </>
          )}
        </div>
      )}

      {/* ================================================================== */}
      {/* Tab: Activity                                                      */}
      {/* ================================================================== */}
      {activeTab === "activity" && (
        <div className={styles.tabContent}>
          <AccountActivityView accountDTO={accountDTO} />
        </div>
      )}

      {/* ================================================================== */}
      {/* Tab: Staff                                                         */}
      {/* ================================================================== */}
      {activeTab === "staff" && isStaffOrSelf && (
        <div className={styles.tabContent}>
          <NotesLookup account={accountDTO} />
          <PunishmentsView account={accountDTO} />
          <AccountActivityView accountDTO={accountDTO} />
        </div>
      )}

      {/* ================================================================== */}
      {/* Tab: Admin Actions                                                 */}
      {/* ================================================================== */}
      {activeTab === "admin" && hasRole(3) && (
        <div className={styles.tabContent}>
          <div className={styles.adminGrid}>
            {/* Moderation */}
            <div className={styles.adminSection}>
              <h4 className={styles.adminSectionTitle}>Moderation</h4>
              <div className={styles.adminActions}>
                {!isBanned && hasRole(5) && (
                  <BanUser accountDTO={accountDTO} onSuccess={handleSuccess} />
                )}
                {isBanned && hasRole(5) && (
                  <UnbanUser accountDTO={accountDTO} onSuccess={handleSuccess} />
                )}
                {hasRole(3) && (
                  <ResetSpawnButton
                    key={modalKey}
                    accountDTO={accountDTO}
                    onModalClose={toggleModalKey}
                  />
                )}
              </div>
            </div>

            {/* Account Management */}
            {hasRole(6) && (
              <div className={styles.adminSection}>
                <h4 className={styles.adminSectionTitle}>Account Management</h4>
                <div className={styles.adminActions}>
                  <AwardBadge
                    userId={accountDTO.id}
                    onSuccess={handleBadgeAwardSuccess}
                  />
                  <ForceUser userId={accountDTO.id} onSuccess={handleSuccess} />
                  <SetAccountNameButton userId={accountDTO.id} />
                  <SetAdminLevelButton
                    userId={accountDTO.id}
                    onSuccess={handleSuccess}
                  />
                </div>
              </div>
            )}

            {/* Session Tools */}
            {hasRole(6) && (
              <div className={styles.adminSection}>
                <h4 className={styles.adminSectionTitle}>Session Tools</h4>
                <div className={styles.adminActions}>
                  <GetSessionButton accountDTO={accountDTO} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// SessionTimeline – shows last session as a mini timeline
// =============================================================================

const SessionTimeline: React.FC<{ session: AccountSessionDTO }> = ({
  session,
}) => {
  const reasonTag = (r: DisconnectReason) => {
    switch (r) {
      case DisconnectReason.crash:
        return <span className={`${styles.timelineTag} ${styles.tagCrash}`}>Crash</span>;
      case DisconnectReason.quit:
        return <span className={`${styles.timelineTag} ${styles.tagQuit}`}>Quit</span>;
      case DisconnectReason.kick:
        return <span className={`${styles.timelineTag} ${styles.tagKick}`}>Kick</span>;
      default:
        return null;
    }
  };

  return (
    <div className={styles.timeline}>
      {session.isOnline ? (
        <div className={styles.timelineItem}>
          <span className={`${styles.timelineDot} ${styles.timelineDotOnline}`} />
          <div className={styles.timelineDate}>
            {formatDateForDisplay(new Date(session.start))}
            <span className={`${styles.timelineTag} ${styles.tagOnline}`}>
              Online
            </span>
          </div>
          <div className={styles.timelineBody}>
            Playing as <strong>{session.characterName}</strong>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.timelineItem}>
            <span className={styles.timelineDot} />
            <div className={styles.timelineDate}>
              {formatDateForDisplay(new Date(session.start))}
            </div>
            <div className={styles.timelineBody}>
              Session started as <strong>{session.characterName}</strong>
            </div>
          </div>
          <div className={styles.timelineItem}>
            <span className={styles.timelineDot} />
            <div className={styles.timelineDate}>
              {formatDateForDisplay(new Date(session.end))}
              {reasonTag(session.disconnectReason)}
            </div>
            <div className={styles.timelineBody}>Session ended</div>
          </div>
        </>
      )}
    </div>
  );
};

export default AccountViewNew;

// Inline component for dot + tooltip logic
function OnlineDotWithTooltip({ lastSession }: { lastSession: AccountSessionDTO | null }) {
  const [hover, setHover] = React.useState(false);
  const isOnline = !!lastSession?.isOnline;
  const since = isOnline ? (lastSession?.start ? new Date(lastSession.start) : null) : (lastSession?.end ? new Date(lastSession.end) : null);
  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <span
        className={isOnline ? styles.onlineDot : styles.offlineDot}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{ cursor: "pointer" }}
      />
      {hover && (
        <span
          style={{
            position: "absolute",
            left: "60%",
            bottom: "120%",
            transform: "translateX(-20%)",
            zIndex: 100,
            pointerEvents: "none"
          }}
        >
          <OnlineTooltip isOnline={isOnline} since={since} />
        </span>
      )}
    </span>
  );
}
